import { useSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Toast } from 'src/components/Toast';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import {
  ImportProgressDataType,
  useGetImporterStatusQuery,
} from 'src/services/api';
import { setImportComplete } from 'src/store/entityImporter/slice';
import {
  alertSnackbar,
  completeProgress,
  setProgressValue,
} from 'src/store/ui/actions';
import { AlertVariant } from 'src/store/ui/types';

export const useImportProgressNotifier = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const entityNameMap: Record<string, string> = {
    client_import: t(($) => $.client, { count: 2 }),
    company_import: t(($) => $.company, { count: 2 }),
  };

  const startedImporterId = useAppSelector((state) => state.entityImports.id);

  const dispatch = useAppDispatch();
  const importType = startedImporterId?.split('|')[1]; // get the import type from the importer id which is in the format of <shortid>|<importType>

  const entityName =
    entityNameMap[importType ?? ''] ?? t(($) => $.client, { count: 2 });
  const { data: importProgressData } = useGetImporterStatusQuery(
    { importerId: startedImporterId || '' },
    { skip: !startedImporterId, pollingInterval: 2000 }, // poll importer progress status every 2 seconds
  );

  const successImportSnackbar = React.useCallback(
    (snackbarKey: string) => (
      <div key={snackbarKey}>
        <Toast
          variant={AlertVariant.SUCCESS}
          onClose={() => closeSnackbar(snackbarKey)}
          description={`${entityName} import completed`}
          actionProps={{
            onClick: () => {
              // refresh the page to view the imported data
              window.location.reload();
            },
            children: `Refresh to view ${entityName.toLowerCase()}`,
            className: 'text-base',
          }}
        />
      </div>
    ),
    [closeSnackbar, entityName],
  );

  /**
   * This function gets the progress data in order to update the progress snack bar value
   * @param progressData:
   */
  const getProgress = (progressData: ImportProgressDataType) => {
    if (!startedImporterId) return;
    const { Progress, Status } = progressData;

    switch (Status) {
      case 'started':
        dispatch(
          setProgressValue(Math.round((Progress.Done / Progress.Total) * 100)),
        );
        break;

      case 'failed':
        dispatch(completeProgress()); // clear the progress snack bar

        dispatch(
          alertSnackbar({
            errorMessage: 'Import runtime failed!',
          }),
        );
        dispatch(setImportComplete(true));
        break;

      case 'completed':
        dispatch(setProgressValue(100)); // TODO: remove this once the backend sends the correct progress value when the import is completed
        dispatch(setImportComplete(true)); // set the import as completed
        dispatch(completeProgress()); // clear the progress snack bar

        // show the success snackbar with an refresh action
        enqueueSnackbar(`${entityName} import completed`, {
          content: (snackbarKey: string) => successImportSnackbar(snackbarKey),
        });

        break;
      default:
        break;
    }
  };

  React.useEffect(() => {
    if (importProgressData) {
      getProgress(importProgressData);
    }
  }, [importProgressData]);
};
